import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
//import { Toolbar, Box } from '@mui/material';
//import './CreateEvent.css';

import {
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Paper,
  IconButton,
  List,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

const CreateEvent = ({ user }) => {
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [timeSessions, setTimeSessions] = useState([]);
  const [extra_questions, setExtraQuestions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [eventData, setEventData] = useState({
    id: "",
    name: "",
    description: "",
    max_players: "",
    accepted_role: "",
    waitlist_role: "",
    reserve: false,
    applications: [],
    status: "draft",
    deadline: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get("/api/discord/getroles", {
          withCredentials: true,
        });
        setRoles(response.data);
      } catch (error) {
        console.error(
          "Error fetching roles:",
          error.response ? error.response.data : error.message
        );
      }
    };

    fetchRoles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedData = {
      ...eventData,
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "reserve" && !checked) {
      updatedData.waitlist_role = ""; // Clear waitlist role when checkbox is unchecked
    }

    setEventData(updatedData);
  };

  const addTimeSession = () => {
    setTimeSessions([...timeSessions, { start: null, end: null }]);
  };

  const updateTimeSession = (index, field, value) => {
    const updatedSessions = [...timeSessions];
    updatedSessions[index][field] = value;
    setTimeSessions(updatedSessions);
  };

  const removeTimeSession = (index) => {
    setTimeSessions(timeSessions.filter((_, i) => i !== index));
  };

  const addExtraQuestion = () => {
    setExtraQuestions([
      ...extra_questions,
      { type: "open-ended", question: "", choices: [], max_length: 100 },
    ]);
  };

  const updateExtraQuestion = (index, field, value) => {
    const updatedQuestions = [...extra_questions];
    updatedQuestions[index][field] = value;
    setExtraQuestions(updatedQuestions);
  };

  const addChoiceToQuestion = (index) => {
    const updatedQuestions = [...extra_questions];
    updatedQuestions[index].choices.push("");
    setExtraQuestions(updatedQuestions);
  };

  const updateChoice = (questionIndex, choiceIndex, value) => {
    const updatedQuestions = [...extra_questions];
    updatedQuestions[questionIndex].choices[choiceIndex] = value;
    setExtraQuestions(updatedQuestions);
  };

  const removeChoice = (questionIndex, choiceIndex) => {
    const updatedQuestions = [...extra_questions];
    updatedQuestions[questionIndex].choices.splice(choiceIndex, 1);
    setExtraQuestions(updatedQuestions);
  };

  const removeExtraQuestion = (index) => {
    setExtraQuestions(extra_questions.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    let newErrors = {};

    if (!eventData.id.trim()) newErrors.id = "Event ID is required";
    if (!eventData.name.trim()) newErrors.name = "Event Name is required";
    if (!eventData.description.trim())
      newErrors.description = "Event Description is required";
    if (!eventData.accepted_role)
      newErrors.accepted_role = "An accepted role must be selected";
    if (timeSessions.length === 0)
      newErrors.timeSessions = "At least one time session is required";
    if (!eventData.deadline)
      newErrors.deadline = "Application deadline is required";
    if (!eventData.max_players)
      newErrors.max_players = "Maximum players must be specified";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setServerMessage(null);
    setIsSubmitting(true);

    const convertedTimeSessions = timeSessions.map((session) => ({
      start: session.start
        ? Math.floor(new Date(session.start).getTime() / 1000)
        : null,
      end: session.end
        ? Math.floor(new Date(session.end).getTime() / 1000)
        : null,
    }));

    const jsonData = {
      ...eventData,
      phases: convertedTimeSessions,
      extra_questions,
      deadline: Math.floor(new Date(eventData.deadline).getTime() / 1000),
    };

    try {
      const response = await axios.post("/api/db/createevent", jsonData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (response.data.redirect) {
        setServerMessage({
          type: "success",
          text: "Event creation successful, redirecting to events page...",
        });
        setTimeout(() => navigate(response.data.redirect), 2000);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "An unexpected error occurred. Please try again.";
      setServerMessage({ type: "error", text: errorMessage });
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2 }} className="create-event-container">
      <Typography variant="h4" gutterBottom>
        Create Event
      </Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Event ID"
              name="id"
              value={eventData.id}
              onChange={handleInputChange}
              error={!!errors.id}
              helperText={errors.id}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Event Name"
              name="name"
              value={eventData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Event Description"
              name="description"
              value={eventData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Acceptee Role</InputLabel>
              <Select
                name="accepted_role"
                value={eventData.accepted_role}
                onChange={handleInputChange}
                error={!!errors.accepted_role}
              >
                <MenuItem value="">Select a Role</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Waitlist Role</InputLabel>
              <Select
                name="waitlist_role"
                value={eventData.waitlist_role}
                onChange={handleInputChange}
                disabled={!eventData.reserve}
              >
                <MenuItem value="">Select a Role</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="reserve"
                  checked={eventData.reserve}
                  onChange={handleInputChange}
                />
              }
              label="Accepts Waitlist"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Time Sessions
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addTimeSession}
              sx={{ mb: 2 }}
              className="neon-glow"
            >
              Add Time Session
            </Button>
            {errors.timeSessions && (
              <Typography color="error" variant="body2">
                {errors.timeSessions}
              </Typography>
            )}
            <List>
              {timeSessions.map((session, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  {/* Session heading */}
                  <Typography variant="h6" gutterBottom>
                    Session {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        selected={session.start}
                        onChange={(date) =>
                          updateTimeSession(index, "start", date)
                        }
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={5}
                        timeCaption="Time"
                        dateFormat="dd-MM-yyyy HH:mm"
                        placeholderText="Select Start Time"
                        customInput={<TextField fullWidth label="Start Time" />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        selected={session.end}
                        onChange={(date) =>
                          updateTimeSession(index, "end", date)
                        }
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="yyyy-MM-dd HH:mm"
                        placeholderText="Select End Time"
                        customInput={<TextField fullWidth label="End Time" />}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => removeTimeSession(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Players"
              name="max_players"
              type="number"
              value={eventData.max_players}
              onChange={handleInputChange}
              error={!!errors.max_players}
              helperText={errors.max_players}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              selected={eventData.deadline}
              onChange={(date) =>
                setEventData({ ...eventData, deadline: date })
              }
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="yyyy-MM-dd HH:mm"
              placeholderText="Select Deadline"
              customInput={
                <TextField
                  fullWidth
                  label="Deadline"
                  error={!!errors.deadline}
                  helperText={errors.deadline}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Extra Questions
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addExtraQuestion}
              sx={{ mb: 2 }}
              className="neon-glow"
            >
              Add Question
            </Button>
            <List>
              {extra_questions.map((question, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Question {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Select
                        fullWidth
                        value={question.type}
                        onChange={(e) =>
                          updateExtraQuestion(index, "type", e.target.value)
                        }
                      >
                        <MenuItem value="open-ended">Open-Ended</MenuItem>
                        <MenuItem value="close-ended">Close-Ended</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Question"
                        value={question.question}
                        onChange={(e) =>
                          updateExtraQuestion(index, "question", e.target.value)
                        }
                      />
                    </Grid>
                    {question.type === "open-ended" && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Max Length"
                          type="number"
                          value={question.max_length}
                          onChange={(e) =>
                            updateExtraQuestion(
                              index,
                              "max_length",
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                    )}
                    {question.type === "close-ended" && (
                      <>
                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => addChoiceToQuestion(index)}
                            className="neon-glow"
                          >
                            Add Choice
                          </Button>
                        </Grid>
                        {question.choices.map((choice, choiceIndex) => (
                          <Grid item xs={12} key={choiceIndex}>
                            <Typography variant="subtitle1" gutterBottom>
                              Choice {choiceIndex + 1}
                            </Typography>
                            <TextField
                              fullWidth
                              label="Choice"
                              value={choice}
                              onChange={(e) =>
                                updateChoice(index, choiceIndex, e.target.value)
                              }
                            />
                            <IconButton
                              onClick={() => removeChoice(index, choiceIndex)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        ))}
                      </>
                    )}
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => removeExtraQuestion(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </List>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="neon-glow"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Snackbar
        open={!!serverMessage}
        autoHideDuration={6000}
        onClose={() => setServerMessage(null)}
      >
        <Alert severity={serverMessage?.type} sx={{ width: "100%" }}>
          {serverMessage?.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateEvent;
